import { format } from "d3-format";
import { DATA_PROTECTION_STATEMENT } from "components/DataProtectionText";
import { localizedNumber } from "shared/numberParser";
import { DEFAULT_MAX_LENGHT } from "./validate";
const CSV_DATA_PROTECTION_TEXT = `${DATA_PROTECTION_STATEMENT}; please use in accordance with the EULA.`;
export const SPECIFIC_CASE_MESSAGE = "Scaling on power input when running this reactor < 60 RPM may cause errors due to a low Re number and the associated flow pattern.";
function formatValue(formatStr, value) {
    return format(formatStr)(value);
}
export const toFixed = (x) => {
    if (Math.abs(x) < 1.0) {
        const e = parseInt(x.toString().split("e-")[1]);
        const s = x.toString().split("e-")[0].replace(".", "");
        if (e) {
            const isNegative = x.toString().startsWith("-");
            x =
                (isNegative ? "-" : "") +
                    "0." +
                    new Array(e).join("0") +
                    s
                        .toString()
                        .substring(isNegative ? 1 : 0)
                        .replace(/\.?0+$/, "");
        }
    }
    else {
        x = parseInt(Number(x).toPrecision());
    }
    return x;
};
function escapeRegExpChars(text) {
    return text.replace(/([.*+?^=!:${}()|[\]/\\])/g, "\\$1");
}
export function highlightMatchingText(text, query) {
    let lastIndex = 0;
    const words = query
        .split(/\s+/)
        .filter((word) => word.length > 0)
        .map(escapeRegExpChars);
    if (words.length === 0) {
        return [text];
    }
    const regexp = new RegExp(words.join("|"), "gi");
    const tokens = [];
    // eslint-disable-next-line no-constant-condition
    while (true) {
        const match = regexp.exec(text);
        if (!match) {
            break;
        }
        const length = match[0].length;
        const before = text.slice(lastIndex, regexp.lastIndex - length);
        if (before.length > 0) {
            tokens.push(before);
        }
        lastIndex = regexp.lastIndex;
        tokens.push(`<b>${match[0]}</b>`);
    }
    const rest = text.slice(lastIndex);
    if (rest.length > 0) {
        tokens.push(rest);
    }
    return tokens.reduce((acc, token) => `${acc}${token}`, "");
}
export const splitThroughPixel = (string, px) => {
    const words = string.split(" ");
    const split = [];
    const div = document.createElement("div");
    div.style.cssText = "white-space:nowrap; display:inline;";
    document.body.appendChild(div);
    for (let i = 0; i < words.length; i++) {
        div.innerText = (div.innerText + " " + words[i]).trim();
        const width = Math.ceil(div.getBoundingClientRect().width);
        if (width > px && div.innerText.split(" ").length > 1) {
            const currentWords = div.innerText.split(" ");
            const lastWord = currentWords.pop();
            split.push(currentWords.join(" "));
            if (lastWord) {
                div.innerText = lastWord;
            }
        }
    }
    if (div.innerText !== "") {
        split.push(div.innerText);
    }
    document.body.removeChild(div);
    return split;
};
export const replaceD3FormatSpecialChars = (value) => value.replace("&nbsp;", "").replace(",", ".").replace("−", "-").replace("−", "-").replace("-", "-");
export const isValueWithNotation = (value) => value.includes("e") || value.includes("E");
const isFloat = (value) => value.includes(".");
export const getDefaultMaxLength = (value) => DEFAULT_MAX_LENGHT + (isNegative(value) ? 1 : 0) + (isFloat(value) ? 1 : 0);
export const isMaxLengthNotReached = (inputString) => inputString.length <= getDefaultMaxLength(inputString);
const isNegative = (value) => value.startsWith("-") || value.startsWith("−");
export const renderNumberValue = (value, { roundingDisabled = false, significantNumbers = 4, transformIndex = 6 } = {}) => {
    if (isNaN(Number(value)) || value == 0) {
        return value?.toString() ?? "";
    }
    if (!value) {
        return "";
    }
    if (roundingDisabled) {
        let roundedValue = value.toString();
        if (isFloat(value.toString())) {
            const [integer, float] = value.toString().split(".");
            const allowedLength = getDefaultMaxLength(value.toString());
            const allowedFloat = allowedLength - integer.length;
            roundedValue =
                allowedLength > value.toString().length
                    ? value.toString()
                    : Number(value)
                        .toFixed(allowedFloat)
                        .substring(0, integer.length + 1 + float.length);
        }
        return isValueWithNotation(roundedValue) ? toFixed(roundedValue) : value.toString();
    }
    const roundedValue = replaceD3FormatSpecialChars(formatValue(`.${significantNumbers}~r`, Number(value)));
    if (`${roundedValue}`.length > transformIndex || isValueWithNotation(roundedValue)) {
        return Number(roundedValue).toExponential(2);
    }
    else {
        return roundedValue;
    }
};
export const numberToFixedPointNotation = (value, fractionDigits = 4) => +format(`.${fractionDigits}~r`)(value).replace("−", "-").replace("−", "-");
export const roundToFactor = (value, factor = 0.5) => Math.round(value / factor) * factor;
export const formatNumberForExport = (number) => {
    const isValueWithNotation = `${number}`.includes("e") || `${number}`.includes("E");
    return `${isValueWithNotation ? renderNumberValue(number) : number}`;
};
export const getNumberFromNumberString = (value, rounding = true) => {
    const { decimal = "," } = localizedNumber.numberSeparators;
    const trimmedStringValue = String(value).replace(decimal, ".").replace(/\s/g, "").replace(/"/g, "");
    return +renderNumberValue(trimmedStringValue, { roundingDisabled: !rounding });
};
export const checkMantisLength = (number = "", limit = 16) => {
    const { decimal = "," } = localizedNumber.numberSeparators;
    return (number.toString().split(decimal)[1]?.length || 0) <= limit;
};
export const convertToCSV = (data) => {
    let str = "";
    data[0].unshift(`"Data Protection"`);
    data[1].unshift(CSV_DATA_PROTECTION_TEXT);
    for (let rowIndex = 0; rowIndex < data.length; rowIndex++) {
        if (rowIndex > 1) {
            data[rowIndex].unshift("");
        }
        str += Object.values(data[rowIndex]).join(",") + "\r\n";
    }
    return str;
};
export const capitalizeFirstLetter = (string = "") => {
    return string.charAt(0).toUpperCase() + string.slice(1);
};
export const capitalizeFirstLetterAfterDot = (string = "") => string.split(".").map(capitalizeFirstLetter).join(".");
export const isHtml = (string = "") => {
    const doc = new DOMParser().parseFromString(string, "text/html");
    return Array.from(doc.body.childNodes).some((node) => node.nodeType === 1);
};
export const getTextContentFromHtml = (target) => {
    if (isHtml(target)) {
        const fakeEl = document.createElement("span");
        fakeEl.innerHTML = target ?? "";
        const text = fakeEl.textContent;
        fakeEl.remove();
        return text;
    }
    else {
        return target;
    }
};
export const sanitizeHtmlTags = (html) => html.replace(/(<([^>]+)>)/gi, "");
export const getTransformYStyleValue = (element) => {
    const style = window.getComputedStyle(element);
    const matrix = new WebKitCSSMatrix(style.transform);
    return matrix.m42;
};
export const convertToNumber = (value, secondValue) => {
    return value ? +value : value == "0" ? 0 : secondValue ? +secondValue : secondValue == "0" ? 0 : null;
};
